import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from "date-fns"
import { EM_DASH } from "./constants"
import type { Locale, RelativeTimeDisplay } from "./types"

/**
 * NOTE: Majority of this file is copied from next-intl
 */
const SECOND = 1
const MINUTE = SECOND * 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24
const WEEK = DAY * 7
const MONTH = DAY * (365 / 12) // Approximation
const QUARTER = MONTH * 3
const YEAR = DAY * 365

const UNIT_SECONDS: Record<Intl.RelativeTimeFormatUnit, number> = {
  second: SECOND,
  seconds: SECOND,
  minute: MINUTE,
  minutes: MINUTE,
  hour: HOUR,
  hours: HOUR,
  day: DAY,
  days: DAY,
  week: WEEK,
  weeks: WEEK,
  month: MONTH,
  months: MONTH,
  quarter: QUARTER,
  quarters: QUARTER,
  year: YEAR,
  years: YEAR,
} as const

function resolveRelativeTimeUnit(seconds: number) {
  const absValue = Math.abs(seconds)
  if (absValue < MINUTE) {
    return "second"
  }
  if (absValue < HOUR) {
    return "minute"
  }
  if (absValue < DAY) {
    return "hour"
  }
  if (absValue < WEEK) {
    return "day"
  }
  if (absValue < MONTH) {
    return "week"
  }
  if (absValue < YEAR) {
    return "month"
  }
  return "year"
}

function calculateRelativeTimeValue(
  seconds: number,
  unit: Intl.RelativeTimeFormatUnit,
) {
  // Round to the nearest integer for that unit
  return Math.round(seconds / UNIT_SECONDS[unit])
}

type BaseOptions = {
  now?: Date
  display?: RelativeTimeDisplay
  futurePrefix?: string
  pastPrefix?: string
}

type StandardRelativeTimeOptions = BaseOptions & {
  unit?: Intl.RelativeTimeFormatUnit
  display?: "standard" | "compact" | "compact-contextual"
}

type SplitRelativeTimeOptions = BaseOptions & {
  units?: SplitDisplayOptions
  display: "split"
}

export type RelativeTimeOptions =
  | StandardRelativeTimeOptions
  | SplitRelativeTimeOptions

type SplitDisplayOptions = {
  hours?: boolean
  minutes?: boolean
  seconds?: boolean
}

function formatDistanceCompact(date: Date | number, now: Date) {
  const dateDate = new Date(date)
  const diffSeconds = (dateDate.getTime() - now.getTime()) / 1000
  const absDiffSeconds = Math.abs(diffSeconds)

  if (absDiffSeconds < MINUTE) {
    const value = Math.round(diffSeconds)
    return `${Math.abs(value)}s`
  }
  if (absDiffSeconds < HOUR) {
    const value = Math.round(diffSeconds / MINUTE)
    return `${Math.abs(value)}m`
  }
  if (absDiffSeconds < DAY) {
    const value = Math.round(diffSeconds / HOUR)
    return `${Math.abs(value)}h`
  }
  if (absDiffSeconds < WEEK) {
    const value = Math.round(diffSeconds / DAY)
    return `${Math.abs(value)}d`
  }
  if (absDiffSeconds < MONTH) {
    const value = Math.round(diffSeconds / WEEK)
    return `${Math.abs(value)}w`
  }
  if (absDiffSeconds < YEAR) {
    const value = Math.round(diffSeconds / MONTH)
    return `${Math.abs(value)}mo`
  }
  const value = Math.round(diffSeconds / YEAR)
  return `${Math.abs(value)}y`
}

function formatDistanceSplit(
  date: Date | number,
  now: Date,
  { hours = true, minutes = true, seconds = true }: SplitDisplayOptions = {},
) {
  const hoursDisplay = Math.abs(differenceInHours(date, now))
  const minutesDisplay = Math.floor(
    Math.abs(differenceInMinutes(date, now)) % 60,
  )
  const secondsDisplay = Math.floor(
    Math.abs(differenceInSeconds(date, now)) % 60,
  )

  return [
    hours ? hoursDisplay : undefined,
    minutes ? minutesDisplay : undefined,
    seconds ? secondsDisplay : undefined,
  ]
    .filter(v => v !== undefined)
    .map(v => String(v).padStart(2, "0"))
    .join(":")
}

export const relativeTimeFormatter =
  (locale: Locale = "en-US") =>
  (
    date: Date | null | undefined,
    options: RelativeTimeOptions = {
      display: "standard",
    },
  ) => {
    if (!date) {
      return EM_DASH
    }

    const dateDate = new Date(date)
    const now = options.now ?? new Date()
    const isPast = dateDate < now

    const getFormattedDate = () => {
      // Handle split format
      if (options.display === "split") {
        return formatDistanceSplit(dateDate, now, options.units)
      }

      // Handle compact format
      if (options.display === "compact") {
        return formatDistanceCompact(dateDate, now)
      }

      // Otherwise, standard format
      const { unit } = options
      const diffSeconds = (dateDate.getTime() - now.getTime()) / 1000
      const resolvedUnit = unit ?? resolveRelativeTimeUnit(diffSeconds)
      const value = calculateRelativeTimeValue(diffSeconds, resolvedUnit)

      const style = options.display === "compact-contextual" ? "narrow" : "long"
      return new Intl.RelativeTimeFormat(locale, {
        // `numeric: 'auto'` can theoretically produce output like "yesterday",
        // but it only works with integers. E.g. -1 day will produce "yesterday",
        // but -1.1 days will produce "-1.1 days". Rounding before formatting is
        // not desired, as the given dates might cross a threshold were the
        // output isn't correct anymore. Example: 2024-01-08T23:00:00.000Z and
        // 2024-01-08T01:00:00.000Z would produce "yesterday", which is not the
        // case. By using `always` we can ensure correct output. The only exception
        // is the formatting of times <1 second as "now".
        numeric: resolvedUnit === "second" ? "auto" : "always",
        style,
        ...options,
      }).format(value, resolvedUnit)
    }

    const { futurePrefix = "", pastPrefix = "" } = options

    const formattedDate = getFormattedDate()

    return isPast
      ? `${pastPrefix}${pastPrefix ? " " : ""}${formattedDate}`
      : `${futurePrefix}${futurePrefix ? " " : ""}${formattedDate}`
  }
